import { useMemo, useState } from "react";

import { useMediaQuery } from "@/hooks/use-media-query";
import { todayIso } from "@/modules/carga-trabalho";
import {
  compararCenario,
  contarPorCategoria,
  filtrarComparacao,
  formatAlteracoes,
  MS,
  rotuloAplicarSimulacao,
} from "@/modules/recursos/simulacao";
import type { FiltroComparacao } from "@/modules/recursos/simulacao";

import type { LegendSwatchKind } from "@/shared/components/legend-swatch";
import { ganttScaleOf } from "@/shared/components/gantt-carga/gantt-scale";
import { CARGA_TRABALHO_MOBILE_QUERY } from "@/shared/components/gantt-carga/mobile";
import {
  COMPARAR_TITULO,
  FILTRO_LABEL,
  INDICADORES_COMPARAR,
  INDICADORES_COMPARAR_PADRAO,
  LEGENDA,
  LEGENDA_SEM_ETAPAS,
} from "./comparar-simulacao-dialog.constants";
import { linhasDaObra, timelineDe } from "./comparar-simulacao-dialog.utils";
import type {
  CompararSimulacaoDialogProps,
  CompararSimulacaoDialogViewModel,
  IndicadoresComparar,
} from "./types-comparar-simulacao-dialog";

/** A legenda segue o que as barras desenham: o Deslocamento acompanha a Obra e o Hoje fica sempre. */
function legendaVisivel(kind: LegendSwatchKind, indicadores: IndicadoresComparar): boolean {
  if (kind === "hoje") return true;
  return indicadores[kind === "deslocamento" ? "obra" : (kind as keyof IndicadoresComparar)];
}

/**
 * "Comparar" (HU 2221): cada obra afetada com as versões Atual e Simulado de cada recurso,
 * filtráveis por tipo. O Atual vem da carga ao vivo; o Simulado, do fold dos itens. Nada é gravado.
 */
export function useCompararSimulacaoDialog({
  cenario,
  cargaAtual = null,
  onClose,
  onAplicar,
}: CompararSimulacaoDialogProps): CompararSimulacaoDialogViewModel {
  const [filtro, setFiltro] = useState<FiltroComparacao>("todos");
  const [indicadores, setIndicadores] = useState<IndicadoresComparar>(INDICADORES_COMPARAR_PADRAO);
  const [indicadoresAberto, setIndicadoresAberto] = useState(false);
  const [recolhidas, setRecolhidas] = useState<ReadonlySet<string>>(new Set());
  const mobile = useMediaQuery(CARGA_TRABALHO_MOBILE_QUERY);

  const contagem = useMemo(() => contarPorCategoria(cenario), [cenario]);
  const todas = useMemo(
    () => compararCenario(cenario, cargaAtual ?? undefined),
    [cenario, cargaAtual],
  );
  const obras = useMemo(() => filtrarComparacao(todas, filtro), [todas, filtro]);
  const timeline = useMemo(() => timelineDe(todas), [todas]);
  const scale = useMemo(() => ganttScaleOf(timeline), [timeline]);
  const hoje = useMemo(() => todayIso(), []);

  const grupos = useMemo(
    () =>
      obras.map((obra) => ({
        chave: obra.id,
        identificacao: obra.identificacao,
        contagem: formatAlteracoes(obra.alteracoes),
        rotuloToggle: `Recolher ou expandir ${obra.identificacao.nome}`,
      })),
    [obras],
  );
  const linhas = useMemo(
    () => obras.flatMap((obra) => linhasDaObra(obra, timeline, indicadores)),
    [obras, timeline, indicadores],
  );
  const gruposAbertos = useMemo(
    () => obras.filter((obra) => !recolhidas.has(obra.id)).map((obra) => obra.id),
    [obras, recolhidas],
  );

  const legenda = LEGENDA.filter(
    (item) =>
      legendaVisivel(item.kind, indicadores) &&
      (filtro === "profissional" || filtro === "todos" || LEGENDA_SEM_ETAPAS.includes(item.kind)),
  );
  const opcoes = (Object.keys(FILTRO_LABEL) as FiltroComparacao[]).map((value) => ({
    value,
    label: `${FILTRO_LABEL[value]} (${contagem[value]})`,
  }));

  function escolherFiltro(valor: FiltroComparacao | null | undefined) {
    if (valor) setFiltro(valor);
  }

  function salvarIndicadores(valor: IndicadoresComparar) {
    setIndicadores(valor);
    setIndicadoresAberto(false);
  }

  function trocarGruposAbertos(abertos: string[]) {
    setRecolhidas(
      new Set(obras.filter((obra) => !abertos.includes(obra.id)).map((obra) => obra.id)),
    );
  }

  return {
    titulo: COMPARAR_TITULO,
    subtitulo: MS.MS001,
    mobile,
    filtro: { valor: filtro, opcoes, escolher: escolherFiltro },
    indicadores: {
      abrir: () => setIndicadoresAberto(true),
      aberto: indicadoresAberto,
      valor: indicadores,
      opcoes: INDICADORES_COMPARAR,
      salvar: salvarIndicadores,
      cancelar: () => setIndicadoresAberto(false),
    },
    legenda,
    grupos,
    linhas,
    gruposAbertos,
    trocarGruposAbertos,
    scale,
    hoje,
    aplicar: onAplicar
      ? { rotulo: rotuloAplicarSimulacao(contagem.todos), executar: onAplicar }
      : null,
    fechar: onClose,
  };
}
